import React, { useState, useEffect } from 'react';
import './ApexIntelligence.css';

const ApexIntelligence: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'trader' | 'swing' | 'investor'>('trader');
  const [showContactForm, setShowContactForm] = useState(false);
  const [waitlistCount] = useState(347); // Simulated waitlist count

  // Scroll animation effect
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in-visible');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.fade-in-section').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="apex-intelligence">
      {/* Hero Section */}
      <section className="apex-hero">
        <div className="container">
          <div className="apex-hero-content">
            <div className="apex-badge">
              <span>Launching Q3 2026</span>
            </div>
            <h1 className="apex-title">
              APEX Intelligence
            </h1>
            <p className="apex-subtitle">
              Peak Market Intelligence
            </p>
            <p className="apex-description">
              Institutional-grade algorithmic trading system that analyzes options market microstructure 
              in real-time. Know when to trade, and more importantly, <strong>when NOT to trade</strong>.
            </p>
            <div className="apex-stats">
              <div className="stat-item">
                <div className="stat-value">68%</div>
                <div className="stat-label">Win Rate</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">2.9:1</div>
                <div className="stat-label">Risk-Reward</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">70%</div>
                <div className="stat-label">Capital Preservation</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">2.1</div>
                <div className="stat-label">Sharpe Ratio</div>
              </div>
            </div>
            <div className="apex-cta-buttons">
              <button className="btn-primary-apex" onClick={() => setShowContactForm(true)}>
                Join Waitlist
              </button>
              <button className="btn-secondary-apex" onClick={() => {
                document.getElementById('apex-features')?.scrollIntoView({ behavior: 'smooth' });
              }}>
                Learn More
              </button>
            </div>
            <div className="waitlist-counter">
              <span className="counter-icon">👥</span>
              <span className="counter-text">
                <strong>{waitlistCount}</strong> traders already on the waitlist
              </span>
            </div>
          </div>
          <div className="apex-hero-visual">
            <div className="dashboard-preview">
              <div className="preview-header">
                <div className="preview-dots">
                  <span></span><span></span><span></span>
                </div>
                <div className="preview-title">APEX Intelligence Dashboard</div>
              </div>
              <div className="preview-content">
                <div className="regime-indicator">
                  <div className="regime-label">Market Regime</div>
                  <div className="regime-value tradeable">TRADEABLE_DIRECTIONAL</div>
                  <div className="regime-confidence">Confidence: 85%</div>
                </div>
                <div className="metrics-grid">
                  <div className="metric-card">
                    <div className="metric-label">VRP</div>
                    <div className="metric-value positive">+8.2%</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-label">Gamma Distance</div>
                    <div className="metric-value">2.3 ATR</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-label">Flow</div>
                    <div className="metric-value strong">STRONG</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-label">Suppression</div>
                    <div className="metric-value low">18%</div>
                  </div>
                </div>
                <div className="signal-box">
                  <div className="signal-icon">✅</div>
                  <div className="signal-text">
                    <div className="signal-action">BUY SIGNAL</div>
                    <div className="signal-detail">NIFTY 23900 CE • Institutional Flow Detected</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem-Solution Section */}
      <section className="apex-problem-solution fade-in-section">
        <div className="container">
          <div className="section-header">
            <h2>The Problem with Retail Trading</h2>
            <p>Why 90% of retail traders lose money</p>
          </div>
          <div className="problem-solution-grid">
            <div className="problem-card">
              <div className="card-icon problem">❌</div>
              <h3>Retail Approach</h3>
              <ul>
                <li>Trade based on price action alone</li>
                <li>Chase breakouts that fail (gamma pinning)</li>
                <li>Trade in dealer-controlled markets (70% of time)</li>
                <li>No understanding of market microstructure</li>
                <li>Emotional decision-making</li>
              </ul>
              <div className="result-badge negative">Result: Consistent Losses</div>
            </div>
            <div className="solution-card">
              <div className="card-icon solution">✅</div>
              <h3>APEX Intelligence</h3>
              <ul>
                <li>See dealer positioning & institutional flow</li>
                <li>Identify true breakouts vs gamma traps</li>
                <li>Avoid dealer-controlled regimes systematically</li>
                <li>Understand WHO controls the market</li>
                <li>Systematic, regime-aware decisions</li>
              </ul>
              <div className="result-badge positive">Result: Institutional Edge</div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Innovation Section */}
      <section className="apex-innovation fade-in-section" id="apex-features">
        <div className="container">
          <div className="section-header">
            <h2>The APEX Difference</h2>
            <p>What makes us institutional-grade</p>
          </div>
          <div className="innovation-grid">
            <div className="innovation-card">
              <div className="innovation-icon">🎯</div>
              <h3>Regime-Aware Trading</h3>
              <p>
                Identifies 5 market regimes. Refuses to trade in DEALER_CONTROLLED environments 
                (40% of time), preserving capital for high-probability setups.
              </p>
              <div className="innovation-stat">70% time in WAIT mode</div>
            </div>
            <div className="innovation-card">
              <div className="innovation-icon">🔬</div>
              <h3>Force Hierarchy Engine</h3>
              <p>
                Answers "WHO controls the market?" - Dealers, Institutions, or Volatility. 
                Quantifies suppression probability (0-100%).
              </p>
              <div className="innovation-stat">V4 Dominance Intelligence</div>
            </div>
            <div className="innovation-card">
              <div className="innovation-icon">⚡</div>
              <h3>Gamma Escape Velocity</h3>
              <p>
                Proprietary metric that quantifies breakout probability using ATR-normalized 
                distance to gamma walls. Avoids false breakouts.
              </p>
              <div className="innovation-stat">90% accuracy on pinning detection</div>
            </div>
            <div className="innovation-card">
              <div className="innovation-icon">📊</div>
              <h3>15+ Institutional Metrics</h3>
              <p>
                Volatility Risk Premium, Dealer Gamma, Flow Confirmation, Vol-of-Vol, 
                Compression Classification, and more - synthesized into single confidence score.
              </p>
              <div className="innovation-stat">Multi-dimensional intelligence</div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Tiers Section */}
      <section className="apex-tiers fade-in-section">
        <div className="container">
          <div className="section-header">
            <h2>Choose Your Intelligence Level</h2>
            <p>From intraday options to long-term investing</p>
          </div>
          <div className="tier-tabs">
            <button 
              className={`tier-tab ${activeTab === 'trader' ? 'active' : ''}`}
              onClick={() => setActiveTab('trader')}
            >
              <img src="/images/APEX_ICON.png" alt="APEX Trader" className="tab-icon-img" />
              <span>APEX Trader</span>
            </button>
            <button 
              className={`tier-tab ${activeTab === 'swing' ? 'active' : ''}`}
              onClick={() => setActiveTab('swing')}
            >
              <span className="tab-icon">📈</span>
              <span>APEX Swing</span>
            </button>
            <button 
              className={`tier-tab ${activeTab === 'investor' ? 'active' : ''}`}
              onClick={() => setActiveTab('investor')}
            >
              <span className="tab-icon">💎</span>
              <span>APEX Investor</span>
            </button>
          </div>

          <div className="tier-content">
            {activeTab === 'trader' && (
              <div className="tier-details">
                <div className="tier-info">
                  <h3>APEX Trader</h3>
                  <p className="tier-subtitle">Intraday Options Trading</p>
                  <div className="tier-price">
                    <span className="price-amount">₹4,999</span>
                    <span className="price-period">/month</span>
                  </div>
                  <ul className="tier-features">
                    <li><span className="feature-icon">✓</span> Real-time options intelligence (7 indices)</li>
                    <li><span className="feature-icon">✓</span> 5-minute regime detection</li>
                    <li><span className="feature-icon">✓</span> Dealer gamma exposure tracking</li>
                    <li><span className="feature-icon">✓</span> Telegram alerts (5-min intervals)</li>
                    <li><span className="feature-icon">✓</span> 98 options contracts monitored</li>
                    <li><span className="feature-icon">✓</span> Intraday strike recommendations</li>
                  </ul>
                  <div className="tier-ideal">
                    <strong>Ideal for:</strong> Active traders, Options sellers, Intraday traders
                  </div>
                  <div className="tier-timeframe">
                    <strong>Timeframe:</strong> 5-minute candles • Holding: Minutes to hours
                  </div>
                </div>
                <div className="tier-visual">
                  <div className="feature-showcase">
                    <div className="showcase-item">
                      <div className="showcase-label">Market Regime</div>
                      <div className="showcase-value">DEALER_CONTROLLED</div>
                      <div className="showcase-action">Action: SIT OUT ⚠️</div>
                    </div>
                    <div className="showcase-item">
                      <div className="showcase-label">Suppression Probability</div>
                      <div className="showcase-value">72%</div>
                      <div className="showcase-action">High dealer control detected</div>
                    </div>
                    <div className="showcase-item">
                      <div className="showcase-label">Next Update</div>
                      <div className="showcase-value">4:32 remaining</div>
                      <div className="showcase-action">Real-time monitoring active</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'swing' && (
              <div className="tier-details">
                <div className="tier-info">
                  <h3>APEX Swing</h3>
                  <p className="tier-subtitle">Swing Trading (3-30 days)</p>
                  <div className="tier-price">
                    <span className="price-amount">₹2,999</span>
                    <span className="price-period">/month</span>
                  </div>
                  <ul className="tier-features">
                    <li><span className="feature-icon">✓</span> Daily stock analysis (large-cap)</li>
                    <li><span className="feature-icon">✓</span> Volatility regime classification</li>
                    <li><span className="feature-icon">✓</span> Delivery volume tracking</li>
                    <li><span className="feature-icon">✓</span> FII/DII flow analysis</li>
                    <li><span className="feature-icon">✓</span> Compression/expansion detection</li>
                    <li><span className="feature-icon">✓</span> Entry/exit timing signals</li>
                  </ul>
                  <div className="tier-ideal">
                    <strong>Ideal for:</strong> Swing traders, Part-time traders, Working professionals
                  </div>
                  <div className="tier-timeframe">
                    <strong>Timeframe:</strong> Daily candles • Holding: 3-30 days
                  </div>
                </div>
                <div className="tier-visual">
                  <div className="feature-showcase">
                    <div className="showcase-item">
                      <div className="showcase-label">RELIANCE</div>
                      <div className="showcase-value">ACCUMULATION</div>
                      <div className="showcase-action">Institutional buying detected</div>
                    </div>
                    <div className="showcase-item">
                      <div className="showcase-label">Delivery %</div>
                      <div className="showcase-value">68%</div>
                      <div className="showcase-action">High conviction buying</div>
                    </div>
                    <div className="showcase-item">
                      <div className="showcase-label">Signal</div>
                      <div className="showcase-value">BUY @ 2450</div>
                      <div className="showcase-action">Target: 2580 • SL: 2390</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'investor' && (
              <div className="tier-details">
                <div className="tier-info">
                  <h3>APEX Investor</h3>
                  <p className="tier-subtitle">Long-term Investing (3+ months)</p>
                  <div className="tier-price">
                    <span className="price-amount">₹1,999</span>
                    <span className="price-period">/month</span>
                  </div>
                  <ul className="tier-features">
                    <li><span className="feature-icon">✓</span> Weekly/monthly stock analysis</li>
                    <li><span className="feature-icon">✓</span> Fundamental + technical fusion</li>
                    <li><span className="feature-icon">✓</span> Institutional ownership tracking</li>
                    <li><span className="feature-icon">✓</span> Sector rotation analysis</li>
                    <li><span className="feature-icon">✓</span> Long-term regime classification</li>
                    <li><span className="feature-icon">✓</span> Portfolio recommendations</li>
                  </ul>
                  <div className="tier-ideal">
                    <strong>Ideal for:</strong> Long-term investors, Portfolio builders, Wealth creators
                  </div>
                  <div className="tier-timeframe">
                    <strong>Timeframe:</strong> Weekly/Monthly candles • Holding: 3 months to 3 years
                  </div>
                </div>
                <div className="tier-visual">
                  <div className="feature-showcase">
                    <div className="showcase-item">
                      <div className="showcase-label">TCS</div>
                      <div className="showcase-value">STRONG BUY</div>
                      <div className="showcase-action">Fundamental Score: 8.5/10</div>
                    </div>
                    <div className="showcase-item">
                      <div className="showcase-label">Institutional Ownership</div>
                      <div className="showcase-value">↑ 3.2%</div>
                      <div className="showcase-action">Increasing for 3 quarters</div>
                    </div>
                    <div className="showcase-item">
                      <div className="showcase-label">Technical Regime</div>
                      <div className="showcase-value">BREAKOUT</div>
                      <div className="showcase-action">52-week high breakout confirmed</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="apex-technology">
        <div className="container">
          <div className="section-header">
            <h2>Powered by Advanced Intelligence</h2>
            <p>Evolution from V1 to V4</p>
          </div>
          <div className="evolution-timeline">
            <div className="evolution-item">
              <div className="evolution-badge">V1</div>
              <h4>Foundation</h4>
              <p>Basic trend detection, VWAP analysis</p>
            </div>
            <div className="evolution-arrow">→</div>
            <div className="evolution-item">
              <div className="evolution-badge">V2</div>
              <h4>Institutional Metrics</h4>
              <p>VRP, Dealer Gamma, Put-Call Skew</p>
            </div>
            <div className="evolution-arrow">→</div>
            <div className="evolution-item">
              <div className="evolution-badge">V3</div>
              <h4>Regime Intelligence</h4>
              <p>5 regimes, Compression classification</p>
            </div>
            <div className="evolution-arrow">→</div>
            <div className="evolution-item active">
              <div className="evolution-badge">V4</div>
              <h4>Force Hierarchy</h4>
              <p>WHO controls market, Suppression probability</p>
            </div>
          </div>
          <div className="tech-features">
            <div className="tech-feature">
              <h4>🔄 Real-Time Processing</h4>
              <p>60-second updates, sub-second analysis</p>
            </div>
            <div className="tech-feature">
              <h4>🧠 15+ Metrics</h4>
              <p>Synthesized into single confidence score</p>
            </div>
            <div className="tech-feature">
              <h4>📊 Multi-Asset</h4>
              <p>7 indices, 98 options contracts</p>
            </div>
            <div className="tech-feature">
              <h4>🎯 Probabilistic</h4>
              <p>Quantified edge, not binary predictions</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials / Results Section */}
      <section className="apex-results fade-in-section">
        <div className="container">
          <div className="section-header">
            <h2>Proven Performance</h2>
            <p>Backtested results from May 2026</p>
          </div>
          <div className="results-grid">
            <div className="result-card">
              <div className="result-icon">📈</div>
              <div className="result-value">68%</div>
              <div className="result-label">Win Rate</div>
              <div className="result-detail">On regime-filtered signals</div>
            </div>
            <div className="result-card">
              <div className="result-icon">💰</div>
              <div className="result-value">2.9:1</div>
              <div className="result-label">Risk-Reward Ratio</div>
              <div className="result-detail">Average win vs average loss</div>
            </div>
            <div className="result-card">
              <div className="result-icon">🛡️</div>
              <div className="result-value">70%</div>
              <div className="result-label">Capital Preservation</div>
              <div className="result-detail">Time in WAIT mode</div>
            </div>
            <div className="result-card">
              <div className="result-icon">⭐</div>
              <div className="result-value">2.1</div>
              <div className="result-label">Sharpe Ratio</div>
              <div className="result-detail">Institutional-grade returns</div>
            </div>
          </div>
          <div className="results-note">
            <p>
              <strong>Key Insight:</strong> The edge comes from NOT trading 70% of the time. 
              By avoiding dealer-controlled regimes, APEX Intelligence preserves capital for 
              high-probability setups, achieving institutional-grade risk-adjusted returns.
            </p>
          </div>
        </div>
      </section>

      {/* Comparison Table Section */}
      <section className="apex-comparison fade-in-section">
        <div className="container">
          <div className="section-header">
            <h2>Why Choose APEX Intelligence?</h2>
            <p>See how we compare to traditional trading and other tools</p>
          </div>
          <div className="comparison-table-container">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th className="feature-col">Feature</th>
                  <th className="competitor-col">Traditional Trading</th>
                  <th className="competitor-col">Other Tools</th>
                  <th className="apex-col">APEX Intelligence</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="feature-name">Regime Detection</td>
                  <td className="competitor-cell">
                    <span className="status-icon negative">❌</span>
                    <span>Manual guesswork</span>
                  </td>
                  <td className="competitor-cell">
                    <span className="status-icon warning">⚠️</span>
                    <span>Basic (2-3 states)</span>
                  </td>
                  <td className="apex-cell">
                    <span className="status-icon positive">✅</span>
                    <span><strong>5 Advanced Regimes</strong></span>
                  </td>
                </tr>
                <tr>
                  <td className="feature-name">Dealer Positioning</td>
                  <td className="competitor-cell">
                    <span className="status-icon negative">❌</span>
                    <span>Not visible</span>
                  </td>
                  <td className="competitor-cell">
                    <span className="status-icon negative">❌</span>
                    <span>Not tracked</span>
                  </td>
                  <td className="apex-cell">
                    <span className="status-icon positive">✅</span>
                    <span><strong>Gamma Exposure Tracking</strong></span>
                  </td>
                </tr>
                <tr>
                  <td className="feature-name">Institutional Flow</td>
                  <td className="competitor-cell">
                    <span className="status-icon negative">❌</span>
                    <span>No visibility</span>
                  </td>
                  <td className="competitor-cell">
                    <span className="status-icon warning">⚠️</span>
                    <span>Basic volume</span>
                  </td>
                  <td className="apex-cell">
                    <span className="status-icon positive">✅</span>
                    <span><strong>Real-time Flow Detection</strong></span>
                  </td>
                </tr>
                <tr>
                  <td className="feature-name">Capital Preservation</td>
                  <td className="competitor-cell">
                    <span className="status-icon negative">❌</span>
                    <span>Trade always</span>
                  </td>
                  <td className="competitor-cell">
                    <span className="status-icon warning">⚠️</span>
                    <span>Basic filters</span>
                  </td>
                  <td className="apex-cell">
                    <span className="status-icon positive">✅</span>
                    <span><strong>70% WAIT Mode</strong></span>
                  </td>
                </tr>
                <tr>
                  <td className="feature-name">Multi-Index Support</td>
                  <td className="competitor-cell">
                    <span className="status-icon warning">⚠️</span>
                    <span>1-2 indices</span>
                  </td>
                  <td className="competitor-cell">
                    <span className="status-icon warning">⚠️</span>
                    <span>3-4 indices</span>
                  </td>
                  <td className="apex-cell">
                    <span className="status-icon positive">✅</span>
                    <span><strong>7 Indices (Equity + Commodity)</strong></span>
                  </td>
                </tr>
                <tr>
                  <td className="feature-name">Real-time Alerts</td>
                  <td className="competitor-cell">
                    <span className="status-icon negative">❌</span>
                    <span>Manual monitoring</span>
                  </td>
                  <td className="competitor-cell">
                    <span className="status-icon positive">✅</span>
                    <span>Email alerts</span>
                  </td>
                  <td className="apex-cell">
                    <span className="status-icon positive">✅</span>
                    <span><strong>Telegram (5-min intervals)</strong></span>
                  </td>
                </tr>
                <tr>
                  <td className="feature-name">Win Rate</td>
                  <td className="competitor-cell">
                    <span className="status-icon negative">📉</span>
                    <span>~45-50%</span>
                  </td>
                  <td className="competitor-cell">
                    <span className="status-icon warning">📊</span>
                    <span>~55-60%</span>
                  </td>
                  <td className="apex-cell">
                    <span className="status-icon positive">📈</span>
                    <span><strong>68% (Backtested)</strong></span>
                  </td>
                </tr>
                <tr>
                  <td className="feature-name">Risk-Reward Ratio</td>
                  <td className="competitor-cell">
                    <span className="status-icon negative">📉</span>
                    <span>~1:1</span>
                  </td>
                  <td className="competitor-cell">
                    <span className="status-icon warning">📊</span>
                    <span>~1.5:1</span>
                  </td>
                  <td className="apex-cell">
                    <span className="status-icon positive">📈</span>
                    <span><strong>2.9:1</strong></span>
                  </td>
                </tr>
                <tr>
                  <td className="feature-name">Support</td>
                  <td className="competitor-cell">
                    <span className="status-icon negative">❌</span>
                    <span>None</span>
                  </td>
                  <td className="competitor-cell">
                    <span className="status-icon warning">⚠️</span>
                    <span>Email only</span>
                  </td>
                  <td className="apex-cell">
                    <span className="status-icon positive">✅</span>
                    <span><strong>Priority Support + Training</strong></span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="comparison-note">
            <p>
              <strong>Bottom Line:</strong> APEX Intelligence provides institutional-grade intelligence 
              that traditional trading and basic tools simply cannot match. Our regime-aware approach 
              preserves capital by avoiding low-edge environments, resulting in superior risk-adjusted returns.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="apex-faq fade-in-section">
        <div className="container">
          <div className="section-header">
            <h2>Frequently Asked Questions</h2>
            <p>Everything you need to know about APEX Intelligence</p>
          </div>
          <div className="faq-grid">
            <div className="faq-item">
              <div className="faq-question">
                <h3>How is APEX Intelligence different from other trading tools?</h3>
              </div>
              <div className="faq-answer">
                <p>
                  APEX Intelligence uses institutional-grade <strong>regime detection</strong> to identify 
                  when dealers control the market. Unlike other tools that generate signals constantly, 
                  we refuse to trade 70% of the time when there's no edge. This capital preservation 
                  approach results in a 68% win rate vs the typical 45-50% for retail traders.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <div className="faq-question">
                <h3>Do I need trading experience to use APEX Intelligence?</h3>
              </div>
              <div className="faq-answer">
                <p>
                  While basic understanding of options trading is helpful, APEX Intelligence is designed 
                  to be accessible. We provide <strong>clear signals</strong> (BUY/SELL/WAIT) with 
                  confidence levels and reasoning. Our training materials and priority support help 
                  beginners understand the institutional perspective. However, we recommend starting 
                  with paper trading to familiarize yourself with the system.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <div className="faq-question">
                <h3>What markets and indices does APEX support?</h3>
              </div>
              <div className="faq-answer">
                <p>
                  <strong>APEX Trader</strong> (Intraday): 7 indices - NIFTY, BANKNIFTY, FINNIFTY, 
                  MIDCPNIFTY, SENSEX (equity) + GOLD, SILVERM (commodities)
                  <br/><br/>
                  <strong>APEX Swing</strong> (3-30 days): Large-cap stocks with liquid options
                  <br/><br/>
                  <strong>APEX Investor</strong> (Long-term): All stocks (large, mid, small cap)
                </p>
              </div>
            </div>

            <div className="faq-item">
              <div className="faq-question">
                <h3>How much capital do I need to start?</h3>
              </div>
              <div className="faq-answer">
                <p>
                  <strong>Minimum recommended:</strong>
                  <br/>
                  • APEX Trader (Options): ₹50,000 - ₹1,00,000
                  <br/>
                  • APEX Swing (Stocks): ₹1,00,000 - ₹2,00,000
                  <br/>
                  • APEX Investor (Long-term): ₹2,00,000+
                  <br/><br/>
                  These amounts allow proper position sizing and risk management. You can start smaller 
                  with paper trading to learn the system risk-free.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <div className="faq-question">
                <h3>Is there a free trial or money-back guarantee?</h3>
              </div>
              <div className="faq-answer">
                <p>
                  Yes! We offer a <strong>30-day money-back guarantee</strong>. If you're not satisfied 
                  with APEX Intelligence within the first 30 days, we'll refund your subscription - 
                  no questions asked. Early bird waitlist members also get <strong>40% off</strong> 
                  the first 3 months.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <div className="faq-question">
                <h3>How do I receive trading signals?</h3>
              </div>
              <div className="faq-answer">
                <p>
                  Signals are delivered via <strong>Telegram</strong> every 5 minutes during market hours. 
                  You'll receive:
                  <br/>
                  • Current market regime (DEALER_CONTROLLED, TRADEABLE, etc.)
                  <br/>
                  • Specific recommendations (BUY 23900 CE, WAIT, AVOID)
                  <br/>
                  • Confidence levels and reasoning
                  <br/>
                  • Key metrics (VRP, Gamma Distance, Flow Strength)
                  <br/><br/>
                  You can also access the web dashboard anytime for detailed analysis.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <div className="faq-question">
                <h3>Can I cancel my subscription anytime?</h3>
              </div>
              <div className="faq-answer">
                <p>
                  Absolutely! There are <strong>no long-term contracts</strong>. You can cancel anytime 
                  from your dashboard. If you cancel, you'll retain access until the end of your current 
                  billing period. We also offer pause options if you need a break.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <div className="faq-question">
                <h3>What kind of support do you provide?</h3>
              </div>
              <div className="faq-answer">
                <p>
                  All APEX Intelligence subscribers get:
                  <br/>
                  • <strong>Priority email support</strong> (24-hour response time)
                  <br/>
                  • <strong>Telegram community</strong> access
                  <br/>
                  • <strong>Weekly Q&A sessions</strong> (live)
                  <br/>
                  • <strong>Training materials</strong> (videos, guides, webinars)
                  <br/>
                  • <strong>System updates</strong> and improvements (free)
                  <br/><br/>
                  Early bird members get 1-on-1 onboarding calls.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <div className="faq-question">
                <h3>When will APEX Intelligence launch?</h3>
              </div>
              <div className="faq-answer">
                <p>
                  We're launching in <strong>Q3 2026 (July-September)</strong>. Waitlist members will 
                  get early access 2 weeks before public launch, plus:
                  <br/>
                  • 40% discount for first 3 months
                  <br/>
                  • Free training materials
                  <br/>
                  • Priority support
                  <br/>
                  • Lifetime "Founding Member" status
                  <br/><br/>
                  Limited to first 100 users. <strong>347 traders</strong> already on the waitlist!
                </p>
              </div>
            </div>

            <div className="faq-item">
              <div className="faq-question">
                <h3>Is APEX Intelligence a "get rich quick" scheme?</h3>
              </div>
              <div className="faq-answer">
                <p>
                  <strong>No.</strong> APEX Intelligence is a professional trading tool, not a magic solution. 
                  Our 68% win rate and 2.9:1 risk-reward are based on backtested results with proper 
                  risk management. Trading involves risk, and past performance doesn't guarantee future 
                  results. APEX helps you make <strong>informed decisions</strong> by showing institutional 
                  intelligence, but you're responsible for your trading decisions and capital management.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="apex-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Join the Waitlist</h2>
            <p>Be among the first to access institutional-grade market intelligence</p>
            <div className="cta-benefits">
              <div className="benefit-item">
                <span className="benefit-icon">🎁</span>
                <span>Early bird discount (40% off)</span>
              </div>
              <div className="benefit-item">
                <span className="benefit-icon">📚</span>
                <span>Free training materials</span>
              </div>
              <div className="benefit-item">
                <span className="benefit-icon">🤝</span>
                <span>Priority support</span>
              </div>
            </div>
            <button className="btn-cta-large" onClick={() => setShowContactForm(true)}>
              Join Waitlist - Launching Q3 2026
            </button>
            <p className="cta-note">Limited to first 100 users • No credit card required</p>
          </div>
        </div>
      </section>

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="modal-overlay" onClick={() => setShowContactForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowContactForm(false)}>×</button>
            <h3>Join APEX Intelligence Waitlist</h3>
            <form className="waitlist-form">
              <div className="form-group">
                <label>Full Name *</label>
                <input type="text" placeholder="Enter your name" required />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input type="email" placeholder="your@email.com" required />
              </div>
              <div className="form-group">
                <label>Phone Number *</label>
                <input type="tel" placeholder="+91 XXXXX XXXXX" required />
              </div>
              <div className="form-group">
                <label>Trading Experience</label>
                <select>
                  <option value="">Select your experience</option>
                  <option value="beginner">Beginner (0-1 year)</option>
                  <option value="intermediate">Intermediate (1-3 years)</option>
                  <option value="advanced">Advanced (3+ years)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Interested In</label>
                <select>
                  <option value="">Select product tier</option>
                  <option value="trader">APEX Trader (Intraday Options)</option>
                  <option value="swing">APEX Swing (Swing Trading)</option>
                  <option value="investor">APEX Investor (Long-term)</option>
                  <option value="all">All Tiers</option>
                </select>
              </div>
              <button type="submit" className="btn-submit">
                Join Waitlist
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApexIntelligence;
